import { LocaleEnum } from '@amzn/fire-tv-campaign-automation-service';
import { CMSConfig } from 'src/configs/campaignMetadataConfig';
import { KatDropdown } from '@amzn/katal-react';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { AutoDismissConfirmationDialog } from 'src/components/general/AutoDismissConfirmationDialog';
import { useSelection } from 'src/contexts/SelectionContext';
import { useAssetOperationsInProgress } from 'src/hooks/useAssetOperationsInProgress';
import { PropUtils } from 'src/utils/PropUtils';
import { useEffect, useState } from 'react';

interface LocaleSelectorProps {}

const LocaleSelector = ({}: LocaleSelectorProps) => {
  const { locale, marketplace, setLocale } = useSelection();
  const isTransforming = useAssetOperationsInProgress();
  // Confirmation state: locale change resets search-based assets
  const [pendingLocale, setPendingLocale] = useState<LocaleEnum | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  // Force dropdown re-render on cancel to reset visual state
  const [dropdownKey, setDropdownKey] = useState(0);

  const handleLocaleChange = (event: KatDropdown.ChangeEvent) => {
    const newLocale = event.detail.value as LocaleEnum;
    if (newLocale !== locale) {
      setPendingLocale(newLocale);
      setShowConfirmation(true);
    }
  };

  const handleConfirmLocaleChange = () => {
    if (pendingLocale) {
      setLocale(pendingLocale);
      setShowConfirmation(false);
      setPendingLocale(null);
    }
  };

  const handleCancelLocaleChange = () => {
    setShowConfirmation(false);
    setPendingLocale(null);
    setDropdownKey((prev) => prev + 1);
  };

  /** When marketplace is changed locale should be updated accordingly */
  useEffect(() => {
    const locales = CMSConfig.LOCALE[marketplace];
    if (locales && locales[0]) {
      setLocale(locales[0]);
    }
  }, [marketplace, setLocale]);

  const localeOptions = PropUtils.createOptionsFromEnumValues(CMSConfig.LOCALE[marketplace] || []);
  return (
    <>
      <StyledDropdown
        key={dropdownKey}
        onChange={handleLocaleChange}
        options={localeOptions}
        label="Select Locale"
        style={{ marginRight: '25px' }}
        value={locale}
        disabled={isTransforming}
      />

      <AutoDismissConfirmationDialog
        isVisible={showConfirmation}
        title="Confirm Locale Change"
        message="Changing locale will reset search-based background images and logo CTAs. Uploaded/URL assets will be preserved. Do you want to continue?"
        confirmText="Continue"
        cancelText="Cancel"
        confirmVariant="primary"
        autoConfirmTimeout={10000}
        onConfirm={handleConfirmLocaleChange}
        onCancel={handleCancelLocaleChange}
        data-testid="locale-confirmation-dialog"
      />
    </>
  );
};

export { LocaleSelector };
